/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: Features section for AI Image Translator - showcasing key capabilities
 */
import Image from 'next/image';
import React from 'react';

import { scrollToTop } from '@/utils/landing/scrollToTop';

interface FeatureItem {
  title: string;
  description: string;
  imageUrl: string;
}

const features: FeatureItem[] = [
  {
    title: 'Precision Text Extraction',
    description:
      "Our AI image translation tool delivers highly accurate text extraction from product labels, posters, screenshots, and other visually complex materials. Advanced OCR captures small characters, curved lines, and multilingual content even when backgrounds are busy or fonts are stylized. By starting with clean, reliable text capture, every translation maintains clarity across different image types. Whether you're working with marketing graphics or scanned documents, the extracted text remains sharp, consistent, and ready for precise translation.",
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/en-to-ko-advertising-poster-before-after.webp',
  },
  {
    title: 'Automatic Language Detection',
    description:
      'Start every translation with the right linguistic context without lifting a finger. Our online image translator automatically identifies the language in your image, eliminating manual setup and reducing the risk of errors. By recognizing mixed scripts and uncommon character sets instantly, FalcoCut removes friction from your workflow and keeps projects moving smoothly. Users serving diverse markets gain faster turnaround, fewer mistakes, and a more reliable path to multilingual communication.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/auto-detect-language-beauty-image.webp',
  },
  {
    title: 'Multi-Language Support',
    description:
      "By supporting over 30 languages, our online AI image translator makes visual content accessible to audiences across regions and industries. Its translation engine delivers natural phrasing that preserves meaning while adapting to different cultural contexts. Whether you're localizing packaging, educational materials, or social content, FalcoCut handles diverse scenarios with ease. With broad language coverage, your visuals can reach more people without additional tools or manual rewriting.",
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/multi-language-support-visual.webp',
  },
  {
    title: 'Dual-Language Localization',
    description:
      "Generate translated images from a single upload, giving you faster multilingual output without doubling your workload. This dual-language workflow keeps your design coherent across regions and updates both versions automatically as you refine the text. Teams expanding into new markets gain a smoother path to localization, reducing turnaround time and improving global communication. Whether you're preparing product visuals or educational materials, producing multiple language versions becomes effortless and reliable.",
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/dual-language-localization-poster.webp',
  },
  {
    title: 'Layout-Preserving Translation',
    description:
      'FalcoCut maintains the original design of your visuals, keeping fonts, spacing, colors, and alignment exactly where they belong. Headlines stay bold, captions remain anchored, and callouts keep their intended position across languages. Instead of spending time fixing reflow issues or adjusting spacing, you get polished results that look natural and professional. This layout-safe approach saves hours of editing and keeps your content visually consistent.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/en-to-fr-layout-preserving-translation-before-after.webp',
  },
  {
    title: 'Confirm Translated Text',
    description:
      'Review the translated text directly on your image before finalizing the result. After the translation is generated, FalcoCut allows you to preview how the translated text appears within the original image layout, including spacing, alignment, and overall visual balance. This confirmation step helps ensure the translation accurately reflects your intended meaning and target language before you proceed. By keeping the original design intact and applying the translated text seamlessly, FalcoCut makes it easy to verify accuracy and consistency without disrupting the visual structure of your image.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/confirm-translated-text-preview.webp',
  },
  {
    title: 'Compatible with Any Image',
    description:
      'Our AI photo translator supports common formats like JPG and PNG, making it easy to translate marketing graphics, signage, app screenshots, and scanned pages. Its OCR engine adapts to different visual styles, ensuring clear text capture regardless of image quality. As a result, both everyday tasks and professional projects receive consistent, dependable translation results. No matter the source, FalcoCut handles diverse visuals with confidence.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/en-to-ja-product-image-translation.webp',
  },
  {
    title: 'Integrated Translation Management',
    description:
      'As an all-in-one image translation platform, FalcoCut lets you upload, detect, translate, and export without switching between tools. Versions stay organized, target languages can be swapped instantly, and translations can be reused across campaigns. You can export finished images for CMS and design workflows. By centralizing the entire process, collaboration becomes smoother and teams work faster with fewer steps.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/image-translation-workflow-upload-translate-download.webp',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

interface FeatureCardProps {
  feature: FeatureItem;
  isReversed: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isReversed }) => {
  return (
    <div
      className={`flex flex-col ${isReversed ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-8 pad:gap-10 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden bg-gray-100">
          <Image
            src={feature.imageUrl}
            alt={feature.title}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <h3 className="text-xl pad:text-2xl font-bold text-gray-900 mb-4">
          {feature.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed mb-6">
          {feature.description}
        </p>
        <button
          type="button"
          onClick={() => scrollToTop()}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
          style={{ background: '#745CFF' }}
        >
          <span>Translate an Image</span>
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
          Unlock Global Reach with AI Image Translation
        </h2>
        <p className="text-sm pad:text-base text-gray-600 text-center max-w-4xl mx-auto mb-12 pad:mb-16">
          FalcoCut brings everything you need to translate images into one platform. From
          accurate text extraction and layout preservation to customizable translations
          and support for multiple formats, our tools make it simple to create clear,
          globally accessible visuals in minutes.
        </p>

        {/* Features List */}
        <div className="space-y-16 pad:space-y-20">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
